import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Dialog,
    DialogContent,
    FormControlLabel,
    RadioGroup,
    Grid,
    Paper,
    Select,
    Typography,
    ListItemIcon,
    Radio,
    Divider,
    MenuItem,
    ListItemText,
    InputLabel,
    FormHelperText
} from "@mui/material/";
import { AuditGroupForm } from "../../../common/components";
import { Add } from "@mui/icons-material";
import { strings } from "../../../common/i18n";
import { format } from "../../../common/lib";
import { useAuditGroupAdmin } from "../../../common/hooks";
import actions from "../../../Store/actions";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles";
import AssetsToAudit from "./Details/AssetsToAudit";
import AuditDetails from "./Details/AuditDetails";

/**
 * Audit Filters Component
 */
export default () => {
    const { auditsCreate, auditGroups, auditFilters, typesList } = useSelector(
        (state) => state
    );
    const enableAuditGroup = useAuditGroupAdmin();
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);

    const [detailValue, setDetailValue] = useState("auditDetails");
    const [disableValue, setDisableValue] = useState({
        auditDetails: false,
        assetsToAudit: true
    });

    const handleAuditDetailChange = async (event) => {
        const { value } = event.target;
        let defaultAuditsCreate = auditsCreate;
        setDisableValue({
            auditDetails: value === "auditDetails" ? false : true,
            assetsToAudit: value === "assetsToAudit" ? false : true
        });

        if (value === "auditDetails") {
            defaultAuditsCreate = {
                assetsToAudit: false,
                filters: {
                    conditions: {
                        isNew: true,
                        isUsed: true
                    },
                    monetaryValueSampling: 0,
                    randomValueSampling: 100
                }
            };
        } else {
            defaultAuditsCreate = {
                assetsToAudit: true,
                filters: {
                    assetsToAudit: []
                }
            };
        }

        await dispatch(
            actions.auditsCreate.create.itemSet(defaultAuditsCreate)
        );
        setDetailValue(value);
    };

    const auditGroup = auditGroups.item || {};
    const onDialogClose = () => setGroupsDialogOpen(false);

    // Branches actually being audited in this batch -- the Branches step is
    // multi-select, and a recommendation is per-branch, so this can be more
    // than one. Mirrors the exact filter AuditSummary.js uses to build the
    // real submission payload.
    const selectedBranchIds = useMemo(() => {
        const rows = auditsCreate.data.filter((item) =>
            auditsCreate.selectedLocations.ids.includes(item.location)
        );
        return [...new Set(rows.map((item) => item.branch))];
    }, [auditsCreate.data, auditsCreate.selectedLocations.ids]);

    useEffect(() => {
        dispatch(actions.auditGroups.create.recommendationClear());
        if (selectedBranchIds.length > 0) {
            dispatch(
                actions.auditGroups.create.getRecommendationRequest(
                    selectedBranchIds
                )
            );
        }
    }, [selectedBranchIds]);

    // Auto-select the recommendation once it loads, but only if nothing is
    // already chosen -- never override a selection the person already made
    // (including going back a step and returning).
    useEffect(() => {
        if (auditGroups.recommendation && !auditGroups.item) {
            dispatch(
                actions.auditGroups.create.itemSet(auditGroups.recommendation)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auditGroups.recommendation]);

    const onAuditGroupChange = (event: Event) => {
        const { value } = event.target;
        if (value === "add") {
            dispatch(actions.auditGroups.create.itemAdd());
            setGroupsDialogOpen(true);
        } else if (value === "recommended") {
            dispatch(
                actions.auditGroups.create.itemSet(auditGroups.recommendation)
            );
        } else {
            const index = auditGroups.byId[value];
            dispatch(
                actions.auditGroups.create.itemSet(auditGroups.data[index])
            );
        }
    };

    /** Get audit group  which are not expired and not closed  */
    const latestAuditGroup = useMemo(() => {
        const { data } = auditGroups;
        const currentDate = new Date();
        const filteredResult = data
            .filter(
                (item) =>
                    new Date(item.endDate) > currentDate && !item.closedDate
            )
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        return filteredResult;
    }, [auditGroups.data]);

    // The recommendation is either a not-yet-existing group (id is null --
    // shown as its own selectable entry, since there's no real MenuItem for
    // it yet) or one that already exists (already present in
    // latestAuditGroup above -- just label that entry instead of duplicating
    // it).
    const { recommendation } = auditGroups;
    const recommendationIsVirtual = recommendation && !recommendation.id;
    const selectedGroupIsVirtualRecommendation =
        auditGroup.isRecommended && !auditGroup.id;
    const selectValue = selectedGroupIsVirtualRecommendation
        ? "recommended"
        : auditGroup.id || "";

    const { auditDetails, assetsToAudit } = disableValue;
    return (
        <>
            <Grid className={classes.detailsRoot} container>
                <Grid item xs={12}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        className={classes.sectionHeader}
                    >
                        {strings("auditGroupDetails")}
                    </Typography>
                    <InputLabel>{strings("group")}*</InputLabel>
                    <Select
                        size="small"
                        onChange={onAuditGroupChange}
                        value={selectValue}
                        style={{ width: "100%" }}
                    >
                        {enableAuditGroup ? (
                            <MenuItem value="add">
                                <ListItemIcon>
                                    <Add color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={strings("add")}
                                    secondary={`${format.dateTimeNew()}`}
                                />
                            </MenuItem>
                        ) : null}
                        <Divider />
                        {recommendationIsVirtual ? (
                            <MenuItem value="recommended">
                                <ListItemText
                                    primary={`${recommendation.name} (${strings(
                                        "recommended"
                                    )})`}
                                    secondary={`${format.dateTimeNew(
                                        recommendation.startDate
                                    )} \u2014 ${format.dateTimeNew(
                                        recommendation.endDate
                                    )}`}
                                />
                            </MenuItem>
                        ) : null}
                        {latestAuditGroup && latestAuditGroup.length > 0 ? (
                            latestAuditGroup.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    <ListItemText
                                        primary={
                                            recommendation &&
                                            recommendation.id === item.id
                                                ? `${item.name} (${strings(
                                                      "recommended"
                                                  )})`
                                                : item.name
                                        }
                                        secondary={`${format.dateTimeNew(
                                            item.startDate
                                        )} \u2014 ${format.dateTimeNew(
                                            item.endDate
                                        )}`}
                                    />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem value="message">
                                <Typography color="error">
                                    {strings("messages.auditGroup")}
                                </Typography>
                            </MenuItem>
                        )}
                    </Select>
                    <FormHelperText>Required</FormHelperText>
                </Grid>
            </Grid>
            <Grid className={classes.detailsRoot} container>
                <Grid item xs={6}>
                    <Paper
                        variant="outlined"
                        className={`${classes.paperContainer}  ${
                            !disableValue.auditDetails
                                ? classes.paperActiveContainer
                                : ""
                        }`}
                    >
                        <RadioGroup
                            aria-label="assetSelection"
                            name="assetSelection"
                            value={detailValue}
                            onChange={handleAuditDetailChange}
                        >
                            <FormControlLabel
                                value="auditDetails"
                                control={<Radio />}
                                label={strings("auditDetails")}
                            />
                        </RadioGroup>
                        <Box
                            component="div"
                            m={1}
                            className={`${
                                auditDetails
                                    ? classes.paperContainerDisable
                                    : ""
                            }`}
                        >
                            <AuditDetails isDisabled={auditDetails} />
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={6}>
                    <Paper
                        variant="outlined"
                        className={`${classes.paperContainer} ${
                            !disableValue.assetsToAudit
                                ? classes.paperActiveContainer
                                : ""
                        }`}
                    >
                        <RadioGroup
                            aria-label="assetSelection"
                            name="assetSelection"
                            value={detailValue}
                            onChange={handleAuditDetailChange}
                        >
                            <FormControlLabel
                                value="assetsToAudit"
                                control={<Radio />}
                                label={strings("specificAssetsToAudit")}
                            />
                        </RadioGroup>
                        <Box
                            component="div"
                            m={1}
                            className={`${
                                assetsToAudit
                                    ? classes.paperContainerDisable
                                    : ""
                            }`}
                        >
                            <AssetsToAudit isDisabled={assetsToAudit} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog onClose={onDialogClose} open={groupsDialogOpen}>
                <DialogContent>
                    <AuditGroupForm onSubmit={onDialogClose} />
                </DialogContent>
            </Dialog>
        </>
    );
};
